import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiRequest } from "../lib/api";
import { normalizeCollection, type ApiRecord } from "../lib/types";
import {
  shouldPlayOrderAlertOnce,
  shouldRepeatOrderAlert,
} from "../../shared/order-alert";
import {
  realtimeReconnectDelay,
  shouldAttemptRealtimeConnection,
} from "../../shared/realtime";

const get = (item: ApiRecord, ...keys: string[]) =>
  keys
    .map((key) => item[key])
    .find((entry) => entry !== undefined && entry !== null);
const asText = (input: unknown, fallback = "") =>
  typeof input === "string" || typeof input === "number"
    ? String(input)
    : fallback;
const asNumber = (input: unknown, fallback = 0) =>
  Number.isFinite(Number(input)) ? Number(input) : fallback;
const asBoolean = (input: unknown) =>
  input === true || input === 1 || input === "1";

function alertSnapshot(items: ApiRecord[]): string {
  return items
    .map((item) =>
      [
        asText(get(item, "branchId", "branch_id")),
        asNumber(get(item, "unconfirmedOrders", "unconfirmed_orders")),
        asText(get(item, "latestOrderUpdatedAt", "latest_order_updated_at")),
      ].join(":"),
    )
    .sort()
    .join("|");
}

function alertUnconfirmedTotal(items: ApiRecord[]): number {
  return items.reduce(
    (total, item) =>
      total + asNumber(get(item, "unconfirmedOrders", "unconfirmed_orders")),
    0,
  );
}

export interface StaffOrderEvent {
  type?: "ORDER_CREATED" | "ORDER_UPDATED";
  orderId?: string;
  orderNumber?: string;
  branchId?: string;
  orderStatus?: string;
  paymentStatus?: string;
  contentChanged?: boolean;
  updatedAt?: string;
}

interface SequencedOrderEvent {
  sequence: number;
  event: StaffOrderEvent;
}

interface OrderAlertContextValue {
  alertSettings: ApiRecord[];
  refreshAlertSettings: () => Promise<ApiRecord[]>;
  realTimeConnected: boolean;
  isLive: boolean;
  muted: boolean;
  setMuted: (value: boolean) => void;
  volume: number;
  setVolume: (value: number) => void;
  soundBlocked: boolean;
  activateSound: () => void;
  unconfirmedTotal: number;
  activeAlarmBranches: number;
  lastEvent?: SequencedOrderEvent;
}

const OrderAlertContext = createContext<OrderAlertContextValue | null>(null);

function readStoredVolume(): number {
  if (typeof window === "undefined") return 0.8;
  const value = Number(
    window.localStorage.getItem("sedapsayur_order_alert_volume"),
  );
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 0.8;
}

function stopAudioElement(audio: HTMLAudioElement | null): void {
  if (!audio) return;
  audio.pause();
  audio.loop = false;
  audio.currentTime = 0;
}

async function startAudioElement(input: {
  current: HTMLAudioElement | null;
  source: string;
  loop: boolean;
  volume: number;
}): Promise<HTMLAudioElement> {
  const absoluteSource = new URL(input.source, window.location.origin).href;
  const sameSource = input.current?.src === absoluteSource;
  const audio = sameSource && input.current
    ? input.current
    : new Audio(input.source);
  if (!sameSource) stopAudioElement(input.current);
  audio.loop = input.loop;
  audio.volume = input.volume;
  audio.muted = false;
  if (!(sameSource && !audio.paused)) await audio.play();
  return audio;
}

export function OrderAlertProvider({ children }: { children: ReactNode }) {
  const [alertSettings, setAlertSettings] = useState<ApiRecord[]>([]);
  const [realTimeConnected, setRealTimeConnected] = useState(false);
  const [fallbackConnected, setFallbackConnected] = useState(false);
  const [muted, setMuted] = useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem("sedapsayur_order_alert_muted") === "1",
  );
  const [volume, setVolume] = useState(readStoredVolume);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const [lastEvent, setLastEvent] = useState<SequencedOrderEvent>();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const settingsRef = useRef<ApiRecord[]>([]);
  const mutedRef = useRef(muted);
  const volumeRef = useRef(volume);
  const previousCountsRef = useRef(new Map<string, number>());
  const eventSequenceRef = useRef(0);

  const refreshAlertSettings = useCallback(async (): Promise<ApiRecord[]> => {
    try {
      const payload = await apiRequest<unknown>("/api/order-alerts");
      const next = normalizeCollection(payload).items;
      settingsRef.current = next;
      setAlertSettings(next);
      return next;
    } catch {
      return settingsRef.current;
    }
  }, []);

  const stopAudio = useCallback(() => {
    stopAudioElement(audioRef.current);
  }, []);

  const playSetting = useCallback(async (setting: ApiRecord, loop: boolean) => {
    if (
      mutedRef.current ||
      !asBoolean(get(setting, "isEnabled", "is_enabled"))
    ) {
      return;
    }
    const source = asText(get(setting, "audioUrl", "audio_url"));
    if (!source) return;
    try {
      audioRef.current = await startAudioElement({
        current: audioRef.current,
        source,
        loop,
        volume: volumeRef.current,
      });
      setSoundBlocked(false);
    } catch {
      setSoundBlocked(true);
    }
  }, []);

  const activateSound = useCallback(() => {
    mutedRef.current = false;
    setMuted(false);
    const selected = settingsRef.current.find(
      (setting) =>
        asBoolean(get(setting, "isEnabled", "is_enabled")) &&
        asNumber(get(setting, "unconfirmedOrders", "unconfirmed_orders")) > 0 &&
        Boolean(get(setting, "audioUrl", "audio_url")),
    );
    if (!selected) {
      setSoundBlocked(false);
      return;
    }
    void playSetting(
      selected,
      asText(get(selected, "playbackMode", "playback_mode")) ===
        "REPEAT_UNTIL_CONFIRMED",
    );
  }, [playSetting]);

  useEffect(() => {
    const timer = window.setTimeout(() => void refreshAlertSettings(), 0);
    return () => window.clearTimeout(timer);
  }, [refreshAlertSettings]);

  useEffect(() => {
    mutedRef.current = muted;
    window.localStorage.setItem(
      "sedapsayur_order_alert_muted",
      muted ? "1" : "0",
    );
    if (audioRef.current) audioRef.current.muted = muted;
    if (muted) stopAudio();
  }, [muted, stopAudio]);

  useEffect(() => {
    const safeVolume = Math.min(1, Math.max(0, volume));
    volumeRef.current = safeVolume;
    window.localStorage.setItem(
      "sedapsayur_order_alert_volume",
      String(safeVolume),
    );
    if (audioRef.current) audioRef.current.volume = safeVolume;
  }, [volume]);

  useEffect(() => {
    settingsRef.current = alertSettings;
    const repeating = alertSettings.find(
      (setting) => shouldRepeatOrderAlert({
        enabled: asBoolean(get(setting, "isEnabled", "is_enabled")),
        hasAudio: Boolean(get(setting, "audioUrl", "audio_url")),
        muted,
        mode: asText(
          get(setting, "playbackMode", "playback_mode"),
          "ONCE",
        ) as "ONCE" | "REPEAT_UNTIL_CONFIRMED",
        unconfirmedOrders: asNumber(
          get(setting, "unconfirmedOrders", "unconfirmed_orders"),
        ),
      }),
    );

    let onceToPlay: ApiRecord | undefined;
    for (const setting of alertSettings) {
      const branchId = asText(get(setting, "branchId", "branch_id"));
      const count = asNumber(
        get(setting, "unconfirmedOrders", "unconfirmed_orders"),
      );
      const previous = previousCountsRef.current.get(branchId);
      previousCountsRef.current.set(branchId, count);
      if (
        !repeating &&
        !onceToPlay &&
        shouldPlayOrderAlertOnce(
          {
            enabled: asBoolean(get(setting, "isEnabled", "is_enabled")),
            hasAudio: Boolean(get(setting, "audioUrl", "audio_url")),
            muted,
            mode: asText(
              get(setting, "playbackMode", "playback_mode"),
              "ONCE",
            ) as "ONCE" | "REPEAT_UNTIL_CONFIRMED",
            unconfirmedOrders: count,
          },
          previous,
        )
      ) {
        onceToPlay = setting;
      }
    }

    if (muted) {
      stopAudio();
      return;
    }
    let playTimer: number | undefined;
    if (repeating) {
      playTimer = window.setTimeout(() => void playSetting(repeating, true), 0);
    } else {
      if (audioRef.current?.loop) stopAudio();
      if (onceToPlay) {
        playTimer = window.setTimeout(
          () => void playSetting(onceToPlay, false),
          0,
        );
      }
    }
    return () => {
      if (playTimer !== undefined) window.clearTimeout(playTimer);
    };
  }, [alertSettings, muted, playSetting, stopAudio]);

  useEffect(() => {
    if (!soundBlocked || muted) return;
    const resume = () => activateSound();
    window.addEventListener("pointerdown", resume, { once: true });
    window.addEventListener("keydown", resume, { once: true });
    return () => {
      window.removeEventListener("pointerdown", resume);
      window.removeEventListener("keydown", resume);
    };
  }, [activateSound, muted, soundBlocked]);

  useEffect(() => {
    let stopped = false;
    let socket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    let heartbeatTimer: number | undefined;
    let watchdogTimer: number | undefined;
    let reconnectAttempt = 0;
    let lastServerContactAt = 0;

    const socketState = () => {
      if (!socket) return "none" as const;
      if (socket.readyState === WebSocket.CONNECTING) return "connecting" as const;
      if (socket.readyState === WebSocket.OPEN) return "open" as const;
      if (socket.readyState === WebSocket.CLOSING) return "closing" as const;
      return "closed" as const;
    };

    const clearSocketTimers = () => {
      if (heartbeatTimer !== undefined) window.clearInterval(heartbeatTimer);
      if (watchdogTimer !== undefined) window.clearInterval(watchdogTimer);
      heartbeatTimer = undefined;
      watchdogTimer = undefined;
    };
    const clearAllTimers = () => {
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
      clearSocketTimers();
    };
    const scheduleReconnect = () => {
      if (
        !shouldAttemptRealtimeConnection({
          stopped,
          browserReportsOnline: navigator.onLine,
          reconnectScheduled: reconnectTimer !== undefined,
          socketState: socketState(),
        })
      ) {
        return;
      }
      const delay = realtimeReconnectDelay(reconnectAttempt);
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delay);
    };
    const connect = () => {
      if (
        !shouldAttemptRealtimeConnection({
          stopped,
          browserReportsOnline: navigator.onLine,
          reconnectScheduled: false,
          socketState: socketState(),
        })
      ) {
        return;
      }
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const candidate = new WebSocket(
        `${protocol}//${window.location.host}/api/events/orders`,
      );
      socket = candidate;
      candidate.addEventListener("open", () => {
        if (stopped || socket !== candidate) return;
        reconnectAttempt = 0;
        lastServerContactAt = Date.now();
        setRealTimeConnected(false);
        void refreshAlertSettings();
        candidate.send("ping");
        clearSocketTimers();
        heartbeatTimer = window.setInterval(() => {
          if (candidate.readyState === WebSocket.OPEN) candidate.send("ping");
        }, 20_000);
        watchdogTimer = window.setInterval(() => {
          if (
            candidate.readyState === WebSocket.OPEN &&
            document.visibilityState === "visible" &&
            Date.now() - lastServerContactAt > 45_000
          ) {
            candidate.close(4000, "Koneksi notifikasi tidak merespons");
          }
        }, 5_000);
      });
      candidate.addEventListener("message", (message) => {
        if (socket !== candidate) return;
        lastServerContactAt = Date.now();
        setFallbackConnected(false);
        setRealTimeConnected(true);
        if (message.data === "pong") return;
        try {
          const event = JSON.parse(String(message.data)) as StaffOrderEvent;
          if (event.type !== "ORDER_CREATED" && event.type !== "ORDER_UPDATED") {
            return;
          }
          eventSequenceRef.current += 1;
          setLastEvent({ sequence: eventSequenceRef.current, event });
          void refreshAlertSettings();
        } catch {
          // Pesan asing diabaikan; koneksi tetap dipertahankan.
        }
      });
      candidate.addEventListener("close", () => {
        if (socket !== candidate) return;
        socket = undefined;
        clearSocketTimers();
        setRealTimeConnected(false);
        scheduleReconnect();
      });
      candidate.addEventListener("error", () => {
        if (socket === candidate) candidate.close();
      });
    };
    const handleOnline = () => {
      if (socket?.readyState === WebSocket.OPEN) socket.send("ping");
      else connect();
    };
    const handleOffline = () => {
      // Edge/Windows dapat memberi sinyal offline palsu. Pertahankan socket
      // yang masih hidup dan biarkan pong server menjadi sumber kebenaran.
      if (socket?.readyState === WebSocket.OPEN) socket.send("ping");
      else scheduleReconnect();
    };
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void refreshAlertSettings();
      if (socket?.readyState === WebSocket.OPEN) {
        lastServerContactAt = Date.now();
        setRealTimeConnected(false);
        socket.send("ping");
      } else connect();
    };

    const handlePageShow = () => {
      handleOnline();
      void refreshAlertSettings();
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibility);
    connect();
    return () => {
      stopped = true;
      clearAllTimers();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibility);
      socket?.close(1000, "Dashboard ditutup");
      stopAudio();
    };
  }, [refreshAlertSettings, stopAudio]);

  useEffect(() => {
    if (realTimeConnected) return undefined;
    let stopped = false;
    let timer: number | undefined;
    let previousSnapshot = alertSnapshot(settingsRef.current);
    let previousTotal = alertUnconfirmedTotal(settingsRef.current);

    const schedule = (delay: number) => {
      if (stopped) return;
      timer = window.setTimeout(() => void poll(), delay);
    };
    const poll = async () => {
      if (document.visibilityState !== "visible") {
        schedule(20_000);
        return;
      }
      try {
        const payload = await apiRequest<unknown>("/api/order-alerts");
        if (stopped) return;
        const next = normalizeCollection(payload).items;
        const nextSnapshot = alertSnapshot(next);
        const nextTotal = alertUnconfirmedTotal(next);
        settingsRef.current = next;
        setAlertSettings(next);
        setFallbackConnected(true);
        if (previousSnapshot && nextSnapshot !== previousSnapshot) {
          eventSequenceRef.current += 1;
          setLastEvent({
            sequence: eventSequenceRef.current,
            event:
              nextTotal > previousTotal
                ? { type: "ORDER_CREATED" }
                : {
                    type: "ORDER_UPDATED",
                    orderId: "__fallback_refresh__",
                    contentChanged: true,
                    updatedAt: new Date().toISOString(),
                  },
          });
        }
        previousSnapshot = nextSnapshot;
        previousTotal = nextTotal;
      } catch {
        if (!stopped) setFallbackConnected(false);
      }
      schedule(20_000);
    };

    schedule(2_500);
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [realTimeConnected]);

  const unconfirmedTotal = useMemo(
    () =>
      alertSettings.reduce(
        (total, setting) =>
          total +
          asNumber(get(setting, "unconfirmedOrders", "unconfirmed_orders")),
        0,
      ),
    [alertSettings],
  );
  const activeAlarmBranches = useMemo(
    () =>
      alertSettings.filter(
        (setting) =>
          asBoolean(get(setting, "isEnabled", "is_enabled")) &&
          Boolean(get(setting, "audioUrl", "audio_url")),
      ).length,
    [alertSettings],
  );
  const value = useMemo<OrderAlertContextValue>(
    () => ({
      alertSettings,
      refreshAlertSettings,
      realTimeConnected,
      isLive: realTimeConnected || fallbackConnected,
      muted,
      setMuted,
      volume,
      setVolume,
      soundBlocked,
      activateSound,
      unconfirmedTotal,
      activeAlarmBranches,
      lastEvent,
    }),
    [
      activateSound,
      activeAlarmBranches,
      alertSettings,
      fallbackConnected,
      lastEvent,
      muted,
      realTimeConnected,
      refreshAlertSettings,
      soundBlocked,
      unconfirmedTotal,
      volume,
    ],
  );

  return (
    <OrderAlertContext.Provider value={value}>
      {children}
    </OrderAlertContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOrderAlert(): OrderAlertContextValue {
  const value = useContext(OrderAlertContext);
  if (!value) {
    throw new Error("useOrderAlert harus digunakan di dalam OrderAlertProvider.");
  }
  return value;
}
